import { create } from 'zustand'
import type { IssueSaveProgressT } from '@/routes/projectStages/issues/lib/createIssue.ts'

export type PublishValidationErrorT = {
  field: 'recipient' | 'status'
  message: string
}

type NewIssueDialogStoreT = {
  dialogOpen: boolean
  toggleDialog: () => void
  updateDialogState: (dialogOpen: boolean) => void
  // "Global mode": the dialog was opened from the stage-wide Report entry
  // point (mobile FAB off the issues list, desktop sidebar button), where the
  // screen is not bound to an issue type, so the user may pick one in the
  // form. Every other open path (dashboard card, list trigger/FAB, bottom
  // sheet, draft opens, map tap) goes through updateDialogState and leaves it
  // false, keeping the type fixed exactly as before.
  issueTypeSelectable: boolean
  setIssueTypeSelectable: (issueTypeSelectable: boolean) => void
  // Opens a fresh create in global mode. The caller sets the default issue
  // type first (useGlobalNewIssueEntry).
  openGlobalNewIssue: () => void
  // The pathname an opener is navigating to while it opens the dialog (the
  // tab-bar sheet: jump to a type's list and open over it). The data router
  // commits that route only after its loaders settle, so the open lands
  // first and the route change later looks like navigating away from an open
  // dialog; useCloseNewIssueDialogOnNavigation keeps the dialog open for this
  // one pathname. Cleared on close.
  keepOpenOnNavigationTo: string | null
  setKeepOpenOnNavigationTo: (pathname: string | null) => void
  draftIssueId: string | null
  setDraftIssueId: (id: string | null) => void
  addingIssueInProgress: boolean
  setAddingIssueInProgress: (addingIssueInProgress: boolean) => void
  // "After saving, add another" toggle. Promoted from local useState in the
  // footer so the mobile top-bar icon-toggle and the desktop footer switch
  // share the same source of truth. The submit hook also reads this to
  // decide whether to closeDialog() or just clear title/description.
  addMoreIssuesAfterSave: boolean
  setAddMoreIssuesAfterSave: (v: boolean) => void
  // Publish-gate errors (recipient/status required by the issue type). Kept
  // here rather than in RHF formState: `setError` on these never-registered,
  // popover-driven fields proved unreliable at re-rendering the dialog's
  // portal subtree (a blocked publish showed no feedback), while a store
  // subscription notifies the alert/chips directly.
  publishValidationErrors: PublishValidationErrorT[]
  setPublishValidationErrors: (errors: PublishValidationErrorT[]) => void
  clearPublishValidationError: (field: PublishValidationErrorT['field']) => void
  // What a failed submit already put into the offline queue (see IssueSaveProgressT), so Save
  // pressed again sends only the rest. Here rather than in the submit hook because the desktop
  // footer and the mobile top bar each run their own instance of that hook, and a retry may come
  // from the other one. createIssue updates the object in place; nothing renders from it.
  issueSaveProgress: IssueSaveProgressT | null
  setIssueSaveProgress: (progress: IssueSaveProgressT | null) => void
}

export const useNewIssueDialogStore = create<NewIssueDialogStoreT>(set => ({
  dialogOpen: false,
  updateDialogState: dialogOpen =>
    set(state => {
      if (dialogOpen) state.setAddingIssueInProgress(true)
      // A repeated "open" on an already open dialog keeps its mode; any real
      // transition (a non-global open, or a close) leaves global mode.
      if (dialogOpen && state.dialogOpen) return { dialogOpen }
      if (!dialogOpen)
        return { dialogOpen, issueTypeSelectable: false, keepOpenOnNavigationTo: null }
      return { dialogOpen, issueTypeSelectable: false }
    }),
  keepOpenOnNavigationTo: null,
  setKeepOpenOnNavigationTo: keepOpenOnNavigationTo => set({ keepOpenOnNavigationTo }),
  issueTypeSelectable: false,
  setIssueTypeSelectable: issueTypeSelectable => set({ issueTypeSelectable }),
  // Clears any stale draftIssueId, as every fresh-create open path does, so
  // the form is not hydrated with a previous draft.
  openGlobalNewIssue: () =>
    set({
      dialogOpen: true,
      addingIssueInProgress: true,
      draftIssueId: null,
      issueTypeSelectable: true,
      keepOpenOnNavigationTo: null,
    }),
  draftIssueId: null,
  setDraftIssueId: draftIssueId => set({ draftIssueId }),
  addingIssueInProgress: false,
  setAddingIssueInProgress: addingIssueInProgress => set({ addingIssueInProgress }),
  addMoreIssuesAfterSave: false,
  setAddMoreIssuesAfterSave: addMoreIssuesAfterSave => set({ addMoreIssuesAfterSave }),
  publishValidationErrors: [],
  setPublishValidationErrors: publishValidationErrors => set({ publishValidationErrors }),
  clearPublishValidationError: field =>
    set(state =>
      state.publishValidationErrors.some(e => e.field === field)
        ? { publishValidationErrors: state.publishValidationErrors.filter(e => e.field !== field) }
        : state,
    ),
  issueSaveProgress: null,
  setIssueSaveProgress: issueSaveProgress => set({ issueSaveProgress }),
  toggleDialog: () =>
    set(state => {
      return state.dialogOpen
        ? { dialogOpen: false, issueTypeSelectable: false, keepOpenOnNavigationTo: null }
        : { dialogOpen: true }
    }),
}))
